<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, toRefs, useTemplateRef, watch} from 'vue';
import {useWindowVirtualizer} from '@tanstack/vue-virtual';
import {debounce} from 'throttle-debounce';
import {SvgIcon} from '../svg.ts';
import {formatDatetimeISO} from '../utils/time.ts';
import {POST} from '../modules/fetch.ts';
import {copyToClipboardWithFeedback} from '../modules/clipboard.ts';
import type {IntervalId} from '../types.ts';
import {toggleFullScreen} from '../utils.ts';
import {localUserSettings} from '../modules/user-settings.ts';
import type {ActionsArtifact, ActionsJob, ActionsRun, ActionsStatus} from '../modules/helmfold-actions.ts';
import {
  type ActionRunViewStore,
  createLogLineMessage,
  type LogLine,
  parseLogLineCommand,
} from './ActionRunView.ts';
import {createLogModel} from './ActionRunLogModel.ts';
import type {LogItem} from './ActionRunLogModel.ts';
import {collapsedAncestorsFor, findFlatIndexForLine, findVisibleIndexForFlatIndex} from './ActionRunLogIndex.ts';
import ActionRunLogRow from './ActionRunLogRow.vue';
import ActionRunLogFindBar from './ActionRunLogFindBar.vue';
import {searchLog} from './ActionRunLogSearch.ts';

type Step = {
  summary: string,
  duration: string,
  status: ActionsStatus,
}

type JobStepState = {
  cursor: string|null,
  expanded: boolean,
  manuallyCollapsed: boolean, // whether the user manually collapsed the step, used to avoid auto-expanding it again
  // true once this step has been marked for the search feature's background fetch (Task 6) --
  // independent of `expanded`: "the server should stream this step's data" is a different
  // question from "the chevron is visually open". See the design spec's "Background
  // step-by-step fetch".
  searchTracked: boolean,
}

type LocaleStorageOptions = {
  autoScroll: boolean;
  expandRunning: boolean;
  actionsLogShowSeconds: boolean;
  actionsLogShowTimestamps: boolean;
};

type CurrentJob = {
  title: string;
  detail: string;
  steps: Array<Step>;
};

type JobData = {
  artifacts: Array<ActionsArtifact>;
  state: {
    run: ActionsRun;
    currentJob: CurrentJob;
  },
  logs: {
    stepsLog?: Array<{
      step: number;
      cursor: string | null;
      started: number;
      lines: LogLine[];
    }>;
  },
};

defineOptions({
  name: 'ActionRunJobView',
});

const props = defineProps<{
  store: ActionRunViewStore,
  jobId: number;
  actionsViewUrl: string;
  locale: Record<string, any>;
}>();
const store = props.store;
const {currentRun: run} = toRefs(store.viewData);

const defaultViewOptions: LocaleStorageOptions = {
  autoScroll: true,
  expandRunning: false,
  actionsLogShowSeconds: false,
  actionsLogShowTimestamps: false,
};

const savedViewOptions = localUserSettings.getJsonObject('actions-view-options', defaultViewOptions);
const {autoScroll, expandRunning, actionsLogShowSeconds, actionsLogShowTimestamps} = savedViewOptions;

// internal state
let loadingAbortController: AbortController | null = null;
let intervalID: IntervalId | null = null;

const currentJobStepsStates = ref<Array<JobStepState>>([]);
const menuVisible = ref(false);
const isFullScreen = ref(false);
const timeVisible = ref<Record<string, boolean>>({
  'log-time-stamp': actionsLogShowTimestamps,
  'log-time-seconds': actionsLogShowSeconds,
});
const optionAlwaysAutoScroll = ref(autoScroll);
const optionAlwaysExpandRunning = ref(expandRunning);
const currentJob = ref<CurrentJob>({
  title: '',
  detail: '',
  steps: [] as Array<Step>,
});
const model = createLogModel();
const visibleItems = shallowRef<LogItem[]>([]);
function syncVisible() {
  visibleItems.value = model.getVisible();
}

const stepStartedAt = ref<Record<number, number>>({});
const rowContainer = useTemplateRef<HTMLElement>('rowContainer');

function estimateSizeFor(item: LogItem | undefined): number {
  // px; a line row is 12px monospace text at the default line-height, a step header is a padded
  // flex row with icons. Both are only first-paint estimates, corrected by measureElement.
  return item?.kind === 'step-header' ? 34 : 16;
}

// Bumped by layout changes that alter row sizes or the container's offsetTop without changing
// `visibleItems`/`rowContainer` -- fullscreen toggle, window resize, timestamp/seconds toggle.
// The options computed reads it (see `void layoutVersion.value` below) so a bump produces a
// fresh options object, re-deriving scrollMargin; on a finished (no longer polling) run nothing
// else would ever invalidate it.
const layoutVersion = ref(0);

const virtualizer = useWindowVirtualizer(computed(() => {
  void layoutVersion.value; // read-only dependency, see above
  return {
    count: visibleItems.value.length,
    estimateSize: (i: number) => estimateSizeFor(visibleItems.value[i]),
    getItemKey: (i: number) => visibleItems.value[i]?.key ?? String(i),
    overscan: 10,
    scrollMargin: rowContainer.value?.offsetTop ?? 0,
  };
}));

// Bump the version first so the fresh options (and scrollMargin) land, then re-measure the rows.
function invalidateLayout() {
  layoutVersion.value++;
  nextTick(() => {
    virtualizer.value.measure();
  });
}

function measureRow(componentInstance: any) {
  const el = componentInstance?.$el as HTMLElement | undefined;
  // $el can be a (truthy) Comment node when a row renders no root element -- e.g. a
  // step-header row without its `step` prop, per ActionRunLogRow.vue's v-if with no v-else.
  // measureElement() would throw on a Comment, so guard for a real element specifically.
  if (el instanceof HTMLElement) virtualizer.value.measureElement(el);
}

// Reusable workflow caller view: the right pane shows just the header (name + uses path +
// status). Callers don't run on a runner, and the dependency graph for their children lives
// in the run summary's WorkflowGraph, not here — matching GitHub Actions.
const selectedJob = computed<ActionsJob | undefined>(() => (run.value.jobs || []).find((it) => it.id === props.jobId));
const isCallerJob = computed(() => Boolean(selectedJob.value?.isReusableCaller));

// a resize changes row heights (lines re-wrap) and the container's offsetTop; debounced because
// resize fires continuously while dragging a window edge.
const onWindowResize = debounce(150, invalidateLayout);

watch(optionAlwaysAutoScroll, () => {
  saveLocaleStorageOptions();
});

watch(optionAlwaysExpandRunning, () => {
  saveLocaleStorageOptions();
});

onMounted(async () => {
  // load job data and then auto-reload periodically
  // need to await first loadJob so this.currentJobStepsStates is initialized and can be used in hashChangeListener
  await loadJob();

  intervalID = setInterval(() => void loadJob(), 1000);
  document.body.addEventListener('click', closeDropdown);
  void hashChangeListener();
  window.addEventListener('hashchange', hashChangeListener);
  window.addEventListener('resize', onWindowResize);
  window.addEventListener('keydown', onGlobalKeydown);
});

onBeforeUnmount(() => {
  document.body.removeEventListener('click', closeDropdown);
  window.removeEventListener('hashchange', hashChangeListener);
  window.removeEventListener('resize', onWindowResize);
  onWindowResize.cancel();
  debouncedSearchScan.cancel();
  searchOpen.value = false; // stop a mid-flight runSearchFetchPump() loop from making further fetches
  window.removeEventListener('keydown', onGlobalKeydown);
  // clear the interval timer when the component is unmounted
  // even our page is rendered once, not spa style
  if (intervalID) {
    clearInterval(intervalID);
    intervalID = null;
  }
});

function saveLocaleStorageOptions() {
  const opts: LocaleStorageOptions = {
    autoScroll: optionAlwaysAutoScroll.value,
    expandRunning: optionAlwaysExpandRunning.value,
    actionsLogShowSeconds: timeVisible.value['log-time-seconds'],
    actionsLogShowTimestamps: timeVisible.value['log-time-stamp'],
  };
  localUserSettings.setJsonObject('actions-view-options', opts);
}

async function copyStepOutput(event: MouseEvent, stepIndex: number) {
  await copyToClipboardWithFeedback(event.currentTarget as HTMLElement, async () => {
    const data = await fetchJobData([{step: stepIndex, cursor: null, expanded: true}]);
    const stepLog = data.logs.stepsLog?.find((s) => s.step === stepIndex);
    const lines: string[] = [];
    for (const line of stepLog?.lines ?? []) {
      const cmd = parseLogLineCommand(line);
      if (cmd?.name === 'hidden' || cmd?.name === 'endgroup') continue;
      const ts = formatDatetimeISO(line.timestamp);
      const msg = createLogLineMessage(line, cmd).textContent ?? '';
      lines.push(`${ts} ${msg}`);
    }
    return lines.join('\n');
  });
}

function isScrolledToBottom(): boolean {
  if (!optionAlwaysAutoScroll.value) return false;
  const v = virtualizer.value;
  return v.scrollOffset != null && v.scrollOffset + v.scrollRect!.height >= v.getTotalSize() - 5;
}

function autoScrollIfAtBottom() {
  if (!isScrolledToBottom()) return;
  const lastIndex = visibleItems.value.length - 1;
  if (lastIndex >= 0) virtualizer.value.scrollToIndex(lastIndex, {align: 'end'});
}

async function toggleStepLogs(step: number) {
  const next = !model.isStepExpanded(step);
  model.setStepExpanded(step, next);
  currentJobStepsStates.value[step].expanded = next;
  syncVisible();
  if (next) {
    void loadJobForce(); // try to load the data immediately instead of waiting for next timer interval
  } else if (currentJob.value.steps[step].status === 'running') {
    currentJobStepsStates.value[step].manuallyCollapsed = true;
  }
  if (optionAlwaysAutoScroll.value && next) {
    await nextTick();
    autoScrollIfAtBottom();
  }
}

async function toggleGroup(key: string) {
  const next = !model.isGroupExpanded(key);
  model.setGroupExpanded(key, next);
  syncVisible();
  if (optionAlwaysAutoScroll.value && next) {
    await nextTick();
    autoScrollIfAtBottom();
  }
}

// "cursor" is used to indicate the last position of the logs.
// It's only used by backend, frontend just reads it and passes it back, it can be any type.
// Frontend knows nothing about its type, never uses its value.
// For example: backend can make cursor=null means the first time to fetch logs, cursor=1234 for a position, cursor=eof for no more logs, etc.
type LogCursor = {step: number, cursor: any, expanded: boolean};

async function fetchJobData(logCursors: LogCursor[], signal?: AbortSignal): Promise<JobData> {
  const resp = await POST(props.actionsViewUrl, {signal, data: {logCursors}});
  return await resp.json();
}

async function loadJobForce() {
  loadingAbortController?.abort();
  loadingAbortController = null;
  await loadJob();
}

async function loadJob() {
  if (loadingAbortController) return;
  const abortController = new AbortController();
  loadingAbortController = abortController;
  try {
    const logCursors = currentJobStepsStates.value.map((it, idx) => ({step: idx, cursor: it.cursor, expanded: it.expanded || it.searchTracked}));
    const runJobResp = await fetchJobData(logCursors, abortController.signal);
    if (loadingAbortController !== abortController) return;

    // FIXME: this logic is quite hacky and dirty, it should be refactored in a better way in the future
    // Use consistent "store" operations to load/update the view data
    store.viewData.runArtifacts = runJobResp.artifacts || [];
    store.viewData.currentRun = runJobResp.state.run;

    currentJob.value = runJobResp.state.currentJob;
    const jobLogs = runJobResp.logs.stepsLog ?? [];

    // sync the currentJobStepsStates to store the job step states
    for (let i = 0; i < currentJob.value.steps.length; i++) {
      const autoExpand = optionAlwaysExpandRunning.value && currentJob.value.steps[i].status === 'running';
      if (!currentJobStepsStates.value[i]) {
        // initial states for job steps
        currentJobStepsStates.value[i] = {cursor: null, expanded: autoExpand, manuallyCollapsed: false, searchTracked: searchOpen.value};
        model.setStepExpanded(i, autoExpand);
      } else {
        // if the step is not manually collapsed by user, then auto-expand it if option is enabled
        if (autoExpand && !currentJobStepsStates.value[i].manuallyCollapsed) {
          currentJobStepsStates.value[i].expanded = true;
          model.setStepExpanded(i, true);
        }
      }
    }

    await nextTick();

    for (let i = 0; i < currentJob.value.steps.length; i++) {
      model.ensureStepHeader(i);
    }
    const shouldAutoScroll = isScrolledToBottom();
    for (const stepLogs of jobLogs) {
      currentJobStepsStates.value[stepLogs.step].cursor = stepLogs.cursor;
      stepStartedAt.value[stepLogs.step] = stepLogs.started;
      model.appendStepLines(stepLogs.step, stepLogs.lines);
    }
    syncVisible();
    if (shouldAutoScroll) autoScrollIfAtBottom();
    if (searchOpen.value && searchQuery.value) debouncedSearchScan();

    // clear the interval timer if the job is done
    if (run.value.done && intervalID) {
      clearInterval(intervalID);
      intervalID = null;
    }
  } catch (e) {
    // avoid network error while unloading page, and ignore "abort" error
    if (e instanceof TypeError || abortController.signal.aborted) return;
    throw e;
  } finally {
    if (loadingAbortController === abortController) loadingAbortController = null;
  }
}

function isDone(status: ActionsStatus) {
  return ['success', 'skipped', 'failure', 'cancelled'].includes(status);
}

function closeDropdown() {
  if (menuVisible.value) menuVisible.value = false;
}

function toggleTimeDisplay(type: 'seconds' | 'stamp') {
  timeVisible.value[`log-time-${type}`] = !timeVisible.value[`log-time-${type}`];
  saveLocaleStorageOptions();
  invalidateLayout(); // showing/hiding the timestamp columns can re-wrap lines, changing row heights
}

function toggleFullScreenMode() {
  isFullScreen.value = !isFullScreen.value;
  toggleFullScreen(document.querySelector('.action-view-right')!, isFullScreen.value, '.action-view-body');
  invalidateLayout(); // re-derive scrollMargin (rowContainer.offsetTop) and remeasure
}

const highlightedKey = ref<string>('');

const searchOpen = ref(false);
const searchQuery = ref('');
const searchCaseSensitive = ref(false);
const searchRegex = ref(false);
const searchMatchKeys = ref<string[]>([]);
const searchInvalidPattern = ref(false);
const currentMatchKey = ref('');
const findBarRef = useTemplateRef<InstanceType<typeof ActionRunLogFindBar>>('findBarRef');

const currentMatchOrdinal = computed(() => {
  const idx = searchMatchKeys.value.indexOf(currentMatchKey.value);
  return idx === -1 ? 0 : idx + 1;
});

// approximate: a step whose fetch is currently in flight (searchTracked already true, data not
// landed yet) isn't counted as "pending" here -- a rough progress indicator, not a precise one.
const pendingSearchStepCount = computed(() =>
  currentJobStepsStates.value.filter((it) => !it.expanded && !it.searchTracked).length,
);

// `allowExpandSearchTracked` distinguishes an explicit navigation act (the user pressing
// next/previous match, or a deep link) from the automatic "jump to the first/newest match"
// that runSearchScan() fires passively whenever new data lands and the current match goes
// stale. The former should behave like any other navigation and expand a search-tracked
// step's chevron so the user can actually see what they asked to jump to; the latter must
// not -- see expandCollapsedAncestors below.
async function jumpToMatch(key: string, allowExpandSearchTracked = true) {
  if (!key) return;
  const flatIdx = model.getFlatIndexForKey(key);
  if (flatIdx === -1) return;
  expandCollapsedAncestors(flatIdx, allowExpandSearchTracked);
  await nextTick();
  // re-resolve: expanding may have loaded more lines, same as hashChangeListener does.
  const resolvedFlatIdx = model.getFlatIndexForKey(key);
  if (resolvedFlatIdx === -1) return;
  // record as current even if it turns out not to be visible/scrollable below, so a subsequent
  // searchNext/searchPrev advances past an unreachable match instead of getting stuck re-selecting it.
  currentMatchKey.value = key;
  const visIdx = findVisibleIndexForFlatIndex(model, resolvedFlatIdx);
  if (visIdx === -1) return;
  virtualizer.value.scrollToIndex(visIdx, {align: 'center'});
}

function runSearchScan() {
  const result = searchLog(model, currentJob.value.steps, searchQuery.value, {
    caseSensitive: searchCaseSensitive.value,
    regex: searchRegex.value,
  });
  searchMatchKeys.value = result.matches;
  searchInvalidPattern.value = result.invalidPattern;
  if (result.matches.length === 0) {
    currentMatchKey.value = '';
    return;
  }
  // if the current match is still a match, leave it (and the scroll position) alone -- only
  // jump when it's genuinely new or the previous one dropped out of the results. This is the
  // automatic/passive jump (not an explicit user navigation act), so it must not pop open a
  // search-tracked step's chevron -- see jumpToMatch/expandCollapsedAncestors.
  if (!result.matches.includes(currentMatchKey.value)) void jumpToMatch(result.matches[0], false);
}
const debouncedSearchScan = debounce(250, runSearchScan);

function onSearchQueryChange(query: string) {
  searchQuery.value = query;
  debouncedSearchScan();
}

function onToggleSearchCase() {
  searchCaseSensitive.value = !searchCaseSensitive.value;
  runSearchScan();
}

function onToggleSearchRegex() {
  searchRegex.value = !searchRegex.value;
  runSearchScan();
}

function searchNext() {
  if (searchMatchKeys.value.length === 0) return;
  const idx = searchMatchKeys.value.indexOf(currentMatchKey.value);
  void jumpToMatch(searchMatchKeys.value[(idx + 1) % searchMatchKeys.value.length]);
}

function searchPrev() {
  if (searchMatchKeys.value.length === 0) return;
  const idx = searchMatchKeys.value.indexOf(currentMatchKey.value);
  const prevIdx = idx <= 0 ? searchMatchKeys.value.length - 1 : idx - 1;
  void jumpToMatch(searchMatchKeys.value[prevIdx]);
}

function openSearch() {
  searchOpen.value = true;
  menuVisible.value = false;
  nextTick(() => findBarRef.value?.focus());
  void runSearchFetchPump();
}

function closeSearch() {
  debouncedSearchScan.cancel();
  searchOpen.value = false;
  searchQuery.value = '';
  searchMatchKeys.value = [];
  currentMatchKey.value = '';
  searchInvalidPattern.value = false;
  for (const state of currentJobStepsStates.value) state.searchTracked = false;
}

let searchPumpRunning = false;

// Fetches not-yet-loaded steps one at a time so search results arrive progressively instead of
// pulling the whole job's remaining log in one response (there's no server-side pagination --
// see the design spec's "Background step-by-step fetch" for why that rules out marking every
// step at once). Started once per search session (from openSearch()); safe to call again while
// already running -- it just no-ops.
async function runSearchFetchPump() {
  if (searchPumpRunning) return;
  searchPumpRunning = true;
  try {
    let index = currentJobStepsStates.value.findIndex((it) => !it.expanded && !it.searchTracked);
    // Caps the "assume this was an abort, retry the same index" case (below) to a single retry
    // per index. Comparing cursorBefore/after can't actually distinguish "this specific fetch got
    // superseded by a competing loadJobForce() call" from "this step's data legitimately never
    // advances its cursor on repeated requests" -- both look identical from here. A genuine abort
    // resolves itself on the very next attempt (the competing call has finished by then), so one
    // retry is enough to recover from it; retrying indefinitely instead turns a step that will
    // never advance into an infinite loop that starves every other step behind it in the queue.
    let retriedCurrentIndex = false;
    while (index !== -1 && searchOpen.value) {
      currentJobStepsStates.value[index].searchTracked = true;
      const cursorBefore = currentJobStepsStates.value[index].cursor;
      await loadJobForce();
      const state = currentJobStepsStates.value[index];
      const stillRunning = currentJob.value.steps[index]?.status === 'running';
      const madeProgress = state!.cursor !== cursorBefore;
      if (!madeProgress && !stillRunning && searchOpen.value && !retriedCurrentIndex) {
        // no progress on a step that isn't itself still running strongly suggests this fetch
        // was aborted by a competing loadJobForce() call (e.g. the user manually toggled a
        // different step) rather than a genuine zero-line result -- searchTracked is already
        // true, so retry the SAME index directly rather than letting the queue scan skip past
        // it (it no longer matches "not yet tracked").
        retriedCurrentIndex = true;
        continue;
      }
      retriedCurrentIndex = false;
      index = currentJobStepsStates.value.findIndex((it) => !it.expanded && !it.searchTracked);
    }
  } finally {
    searchPumpRunning = false;
  }
}

function isForeignTextInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return false;
  const barEl = (findBarRef.value as unknown as {$el?: Node} | null)?.$el;
  return !(barEl && barEl.contains(target));
}

function onGlobalKeydown(event: KeyboardEvent) {
  if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'f') return;
  if (isForeignTextInput(event.target)) return;
  // .job-step-container uses v-show, not v-if, so rowContainer.value stays truthy even when the
  // panel is hidden (a reusable-workflow caller job, or a job with zero steps) -- mirror the same
  // condition the template uses for that v-show.
  if (isCallerJob.value || !currentJob.value.steps.length) return;
  event.preventDefault();
  if (searchOpen.value) {
    findBarRef.value?.focus();
  } else {
    openSearch();
  }
}

// Loops collapsedAncestorsFor to a fixed point: each call resolves at most one level of
// collapse (the nearest enclosing collapsed step, or -- once the step is expanded -- the
// nearest enclosing collapsed group), so a target nested inside both a collapsed step AND a
// collapsed group needs two calls to fully reach. The 16-iteration cap is far past any
// realistic nesting depth; it's a safety bound, not an expected path.
//
// `allowExpandSearchTracked` (default true: every caller except runSearchScan's automatic jump
// wants normal behavior) gates whether a step that's collapsed ONLY because the background
// search fetch (Task 6) marked it searchTracked -- not because the user opened it -- is allowed
// to pop its chevron open here. The automatic "jump to the newest match" must not (the whole
// point of search-tracking is loading a step's data without visually expanding it), but an
// EXPLICIT navigation act -- the user pressing next/previous match, or a hash deep link -- should
// expand it like any other collapsed step, or the user could never actually reach/see a match
// living inside a search-tracked step.
function expandCollapsedAncestors(flatIdx: number, allowExpandSearchTracked = true) {
  for (let iterations = 0; iterations < 16; iterations++) {
    const ancestors = collapsedAncestorsFor(model, flatIdx);
    if (ancestors.length === 0) return;
    for (const ancestor of ancestors) {
      if (ancestor.type === 'step') {
        const stepState = currentJobStepsStates.value[ancestor.step];
        // Leave it collapsed (and don't visit anything nested further inside it either -- there's
        // nothing useful to reveal below a step whose content isn't shown); jumpToMatch still
        // records the match as current and updates the match counter, it just can't scroll to or
        // highlight a row that isn't rendered.
        if (stepState.searchTracked && !stepState.expanded && !allowExpandSearchTracked) return;
        stepState.expanded = true;
        model.setStepExpanded(ancestor.step, true);
      } else {
        model.setGroupExpanded(ancestor.groupKey, true);
      }
    }
    syncVisible();
  }
}

async function hashChangeListener() {
  const match = /^#jobstep-(\d+)-(\d+)$/.exec(window.location.hash);
  if (!match) {
    highlightedKey.value = ''; // navigating away from a line target clears the previous highlight
    return;
  }
  const step = Number(match[1]);
  const targetLine = Number(match[2]);
  if (!currentJobStepsStates.value[step]) return;
  if (!currentJobStepsStates.value[step].expanded && currentJobStepsStates.value[step].cursor === null) {
    currentJobStepsStates.value[step].expanded = true;
    model.setStepExpanded(step, true);
    await loadJob(); // first load for this step, same as today
  }
  let flatIdx = findFlatIndexForLine(model, step, targetLine);
  if (flatIdx === -1) return; // e.g. targets a 'hidden' line -- no-op, matches today's behavior
  expandCollapsedAncestors(flatIdx);
  await nextTick();
  flatIdx = findFlatIndexForLine(model, step, targetLine); // re-resolve: expanding may have loaded more lines
  const visIdx = findVisibleIndexForFlatIndex(model, flatIdx);
  if (visIdx === -1) return;
  // store the item's stable key, not its flat index: a later append into an earlier step shifts
  // every following flat index, which would silently move the highlight to a different line.
  highlightedKey.value = model.getFlat()[flatIdx].key;
  virtualizer.value.scrollToIndex(visIdx, {align: 'center'});
}
</script>
<template>
  <div class="job-info-header">
    <div class="job-info-header-left gt-ellipsis">
      <div class="job-info-header-title-row">
        <h3 class="job-info-header-title gt-ellipsis">
          {{ isCallerJob ? selectedJob?.name : currentJob.title }}
        </h3>
        <span v-if="isCallerJob && selectedJob?.callUses" class="ui label job-info-header-uses">
          <span>uses:</span>
          <span class="gt-ellipsis">{{ selectedJob.callUses }}</span>
        </span>
      </div>
      <p class="job-info-header-detail">
        {{ isCallerJob && selectedJob ? locale.status[selectedJob.status] : currentJob.detail }}
      </p>
    </div>
    <div class="job-info-header-right">
      <div class="ui top right pointing dropdown custom jump item" @click.stop="menuVisible = !menuVisible" @keyup.enter="menuVisible = !menuVisible">
        <button class="btn interact-bg tw-p-2">
          <SvgIcon name="octicon-gear" :size="18"/>
        </button>
        <div class="menu transition action-job-menu" :class="{visible: menuVisible}" v-if="menuVisible" v-cloak>
          <a :class="['item log-find-open-item', !currentJob.steps.length ? 'disabled' : '']" @click="openSearch">
            <i class="icon"><SvgIcon name="octicon-search"/></i>
            {{ locale.findInLog }}
          </a>
          <div class="divider"/>
          <a class="item" @click="toggleTimeDisplay('seconds')">
            <i class="icon"><SvgIcon :name="timeVisible['log-time-seconds'] ? 'octicon-check' : 'helmfold-empty-checkbox'"/></i>
            {{ locale.showLogSeconds }}
          </a>
          <a class="item" @click="toggleTimeDisplay('stamp')">
            <i class="icon"><SvgIcon :name="timeVisible['log-time-stamp'] ? 'octicon-check' : 'helmfold-empty-checkbox'"/></i>
            {{ locale.showTimeStamps }}
          </a>
          <a class="item" @click="toggleFullScreenMode()">
            <i class="icon"><SvgIcon :name="isFullScreen ? 'octicon-check' : 'helmfold-empty-checkbox'"/></i>
            {{ locale.showFullScreen }}
          </a>
          <div class="divider"/>
          <a class="item" @click="optionAlwaysAutoScroll = !optionAlwaysAutoScroll">
            <i class="icon"><SvgIcon :name="optionAlwaysAutoScroll ? 'octicon-check' : 'helmfold-empty-checkbox'"/></i>
            {{ locale.logsAlwaysAutoScroll }}
          </a>
          <a class="item" @click="optionAlwaysExpandRunning = !optionAlwaysExpandRunning">
            <i class="icon"><SvgIcon :name="optionAlwaysExpandRunning ? 'octicon-check' : 'helmfold-empty-checkbox'"/></i>
            {{ locale.logsAlwaysExpandRunning }}
          </a>
          <div class="divider"/>
          <a :class="['item', !currentJob.steps.length ? 'disabled' : '']" :href="run.link + '/jobs/' + jobId + '/logs'" download>
            <i class="icon"><SvgIcon name="octicon-download"/></i>
            {{ locale.downloadLogs }}
          </a>
        </div>
      </div>
    </div>
  </div>
  <div
    class="job-step-container"
    v-show="!isCallerJob && currentJob.steps.length"
    :class="{
      'log-line-show-timestamps': timeVisible['log-time-stamp'],
      'log-line-show-seconds': timeVisible['log-time-seconds']
    }"
  >
    <!-- getTotalSize() already returns a container-relative height (virtual-core computes it as
         `lastMeasurement.end - scrollMargin + paddingEnd`), matching the rows' own
         `vItem.start - scrollMargin` positioning -- so it must NOT subtract scrollMargin again.
         The rowContainer ref sits here rather than on .job-step-container so that scrollMargin
         (its offsetTop) accounts for that element's padding, which offsets the rows. -->
    <div ref="rowContainer" :style="{height: `${virtualizer.getTotalSize()}px`, position: 'relative'}">
      <template v-for="vItem in virtualizer.getVirtualItems()" :key="vItem.key as string">
        <!-- the v-if guards the window where the model and currentJob.steps momentarily disagree -->
        <ActionRunLogRow
          v-if="visibleItems[vItem.index]"
          :ref="measureRow"
          :data-index="vItem.index"
          :style="{
            position: 'absolute', top: 0, left: 0, width: '100%',
            transform: `translateY(${vItem.start - virtualizer.options.scrollMargin!}px)`,
          }"
          :item="visibleItems[vItem.index]"
          :step="visibleItems[vItem.index].kind === 'step-header' ? currentJob.steps[visibleItems[vItem.index].step] : undefined"
          :started-at="stepStartedAt[visibleItems[vItem.index].step] ?? 0"
          :expanded="visibleItems[vItem.index].kind === 'step-header'
            ? currentJobStepsStates[visibleItems[vItem.index].step]?.expanded ?? false
            : model.isGroupExpanded(visibleItems[vItem.index].key)"
          :highlighted="visibleItems[vItem.index].key === highlightedKey"
          :is-search-match="visibleItems[vItem.index].key === currentMatchKey"
          :is-done-run="isDone(run.status)"
          :loading="currentJobStepsStates[visibleItems[vItem.index].step]?.cursor === null"
          :locale="locale"
          @toggle-step="toggleStepLogs"
          @toggle-group="toggleGroup"
          @copy-step="(step, event) => copyStepOutput(event, step)"
        />
      </template>
    </div>
  </div>
  <ActionRunLogFindBar
    v-if="searchOpen"
    ref="findBarRef"
    :query="searchQuery"
    :match-count="searchMatchKeys.length"
    :current-match-ordinal="currentMatchOrdinal"
    :pending-step-count="pendingSearchStepCount"
    :case-sensitive="searchCaseSensitive"
    :regex="searchRegex"
    :invalid-pattern="searchInvalidPattern"
    :locale="locale"
    class="log-find-bar-mount"
    @query-change="onSearchQueryChange"
    @next="searchNext"
    @prev="searchPrev"
    @toggle-case="onToggleSearchCase"
    @toggle-regex="onToggleSearchRegex"
    @close="closeSearch"
  />
</template>
<style scoped>
/* begin fomantic dropdown menu overrides */

.action-view-right .ui.dropdown .menu {
  background: var(--color-console-menu-bg);
  border-color: var(--color-console-menu-border);
}

.action-view-right .ui.dropdown .menu > .item {
  color: var(--color-console-fg);
}

.action-view-right .ui.dropdown .menu > .item:hover {
  color: var(--color-console-fg);
  background: var(--color-console-hover-bg);
}

.action-view-right .ui.dropdown .menu > .item:active {
  color: var(--color-console-fg);
  background: var(--color-console-active-bg);
}

.action-view-right .ui.dropdown .menu > .divider {
  border-top-color: var(--color-console-menu-border);
}

.action-view-right .ui.pointing.dropdown > .menu:not(.hidden)::after {
  background: var(--color-console-menu-bg);
  box-shadow: -1px -1px 0 0 var(--color-console-menu-border);
}

/* end fomantic dropdown menu overrides */

.job-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 12px;
  position: sticky;
  top: 0;
  height: 60px;
  z-index: 1; /* above .job-step-container */
  background: var(--color-console-bg);
  border-radius: 3px;
}

.job-info-header:has(+ .job-step-container) {
  border-radius: var(--border-radius) var(--border-radius) 0 0;
}

.job-info-header .job-info-header-title {
  color: var(--color-console-fg);
  font-size: 16px;
  margin: 0;
}

.job-info-header .job-info-header-detail {
  color: var(--color-console-fg-subtle);
  font-size: 12px;
}

.job-info-header-left {
  flex: 1;
  min-width: 0;
}

.job-info-header-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.job-info-header-uses {
  display: inline-flex !important;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.job-step-container {
  /* the container-level equivalent of the pre-virtualization `.job-step-section { margin: 10px }`
     that wrapped each step's header and log block. Padding, not margin: the container is visually
     joined to .job-info-header above it (shared border-radius + border-top), and the fullscreen
     rule sizes it with height: calc(100% - 60px), which padding stays inside of (border-box). */
  padding: 10px;
  max-height: 100%;
  border-radius: 0 0 var(--border-radius) var(--border-radius);
  border-top: 1px solid var(--color-console-border);
  z-index: 0;
}

.log-find-bar-mount {
  position: absolute;
  /* below .job-info-header (60px tall, with 8px clearance) so the find bar doesn't overlap the
     gear/settings button it was opened from. */
  top: 68px;
  right: 12px;
  z-index: 2; /* above .job-info-header's sticky z-index: 1 */
}
</style>

<style> /* eslint-disable-line vue-scoped-css/enforce-style-type */
/* some elements are not managed by vue, so we need to use global style */

/* selectors here are intentionally exact to only match fullscreen */

.full.height > .action-view-right {
  width: 100%;
  height: 100%;
  padding: 0;
  border-radius: 0;
}

.full.height > .action-view-right > .job-info-header {
  border-radius: 0;
}

.full.height > .action-view-right > .job-step-container {
  height: calc(100% - 60px);
  border-radius: 0;
}
</style>
